import { useState } from 'react';
import { useApplication } from '../../../hooks/queries/useApplications';
import { useInterview } from '../../../hooks/queries/useInterviews';
import { useUpdateApplicationStatus } from '../../../hooks/mutations/useApplicationMutations';
import { useUpdateInterview } from '../../../hooks/mutations/useInterviewMutations';
import ApplicantQA from '../../../components/applicant/ApplicantQA';

type TabView = 'application' | 'notes';

interface ManagerInterviewDetailProps {
  applicationId?: string;
  interviewId?: string;
  // Legacy prop — fall back when only candidateId is available
  candidateId?: string;
}

export default function ManagerInterviewDetail({
  applicationId,
  interviewId,
  candidateId,
}: ManagerInterviewDetailProps) {
  const resolvedInterviewId = interviewId ?? '';

  const [activeTab, setActiveTab] = useState<TabView>('application');
  const [notes, setNotes] = useState('');
  const [notesSaved, setNotesSaved] = useState(false);
  const [checkedQuestions, setCheckedQuestions] = useState<Set<string>>(new Set());
  const [rating, setRating] = useState(3); // 1=Bad, 2=OK, 3=Good, 4=Great

  const { data: interview, isLoading: intLoading } = useInterview(resolvedInterviewId);

  // Resolve applicationId: prefer explicit prop, then from loaded interview, then candidateId fallback
  const resolvedApplicationId = applicationId ?? interview?.applicationId ?? candidateId ?? '';

  const { data: application, isLoading: appLoading, isError: appError } = useApplication(resolvedApplicationId, 'manager');
  const updateStatus = useUpdateApplicationStatus();
  const updateInterview = useUpdateInterview();

  const ratingLabels = ['1-Bad', '2-OK', '3-Good', '4-Great'];
  const ratingPercentage = ((rating - 1) / 3) * 100;

  const handleQuestionToggle = (questionId: string) => {
    setCheckedQuestions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(questionId)) {
        newSet.delete(questionId);
      } else {
        newSet.add(questionId);
      }
      return newSet;
    });
  };

  const handleReject = () => {
    if (!resolvedApplicationId) return;
    updateStatus.mutate({ applicationId: resolvedApplicationId, payload: { status: 'rejected' } });
  };

  const handleBench = () => {
    if (!resolvedApplicationId) return;
    updateStatus.mutate({ applicationId: resolvedApplicationId, payload: { status: 'bench' } });
  };

  const handleHire = () => {
    if (!resolvedApplicationId) return;
    updateStatus.mutate({ applicationId: resolvedApplicationId, payload: { status: 'offer' } });
  };

  const handleSaveNotes = () => {
    if (!resolvedInterviewId) return;
    updateInterview.mutate(
      { interviewId: resolvedInterviewId, payload: { feedbackNotes: notes } },
      { onSuccess: () => setNotesSaved(true) }
    );
  };

  if (intLoading || (resolvedApplicationId && appLoading)) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <p className="text-sm text-gray-400">Loading…</p>
      </div>
    );
  }

  if (appError || (!appLoading && resolvedApplicationId && !application)) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <p className="text-sm text-red-500">Failed to load candidate details.</p>
      </div>
    );
  }

  // Interview questions come from the job posting (via the loaded interview record)
  // For now map application questions to an interview script format
  const interviewQuestions: Array<{ id: string; question: string }> =
    application?.questions.map((q) => ({ id: q.id, question: q.prompt })) ?? [];

  const VIDEO_POSTER =
    'https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2';

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Tab Selector (at top, no header) */}
      <div className="bg-gray-50 px-4 py-3 sticky top-0 z-10 border-b border-gray-100">
        <div className="bg-gray-100 rounded-xl p-1 flex">
          <button
            type="button"
            onClick={() => setActiveTab('application')}
            className={`flex-1 py-2 px-4 rounded-lg text-sm font-semibold transition-all ${
              activeTab === 'application'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Application
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('notes')}
            className={`flex-1 py-2 px-4 rounded-lg text-sm font-semibold transition-all ${
              activeTab === 'notes'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Notes & Script
          </button>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto">
        {activeTab === 'application' && (
          <div>
            {/* Video with Name Overlay */}
            <div className="relative">
              <video
                className="w-full h-[320px] sm:h-[360px] object-cover"
                poster={VIDEO_POSTER}
                controls
                playsInline
              />

              {/* Gradient overlay */}
              <div className="absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-black/80 to-transparent" />

              {/* Name overlay */}
              <div className="absolute bottom-0 left-0 right-0 px-5 pb-5 z-10">
                <h1 className="text-2xl font-bold text-white">{application.name}</h1>
                <p className="text-sm text-gray-200 mt-1">{application.jobRole}</p>
              </div>
            </div>

            {/* Status and Scores Row */}
            <div className="grid grid-cols-[auto_1fr_1fr] gap-4 border-b border-gray-100 items-center px-5 py-3">
              {/* Status Pill */}
              <div>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide bg-green-100 text-green-700">
                  {application.status}
                </span>
              </div>

              {/* Schedule Fit */}
              <div className="text-left px-3 py-2">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                  Schedule Fit
                </p>
                <div className="h-1.5 rounded-full bg-gray-200 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-orange-600 to-orange-500"
                    style={{ width: `${application.scheduleFit}%` }}
                  />
                </div>
              </div>

              {/* AI Score */}
              <div className="text-left px-3 py-2">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                  AI Score
                </p>
                <div className="h-1.5 rounded-full bg-gray-200 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-purple-600 to-purple-500"
                    style={{ width: `${application.aiScore}%` }}
                  />
                </div>
              </div>
            </div>

            {/* Q&A */}
            <section className="px-5 py-6">
              <ApplicantQA questions={application.questions} layout="compact" />
            </section>
          </div>
        )}

        {activeTab === 'notes' && (
          <div className="p-4 space-y-6">
            {/* Interview Script */}
            <div>
              <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-3">
                Suggested Questions
              </h2>
              {interviewQuestions.length > 0 ? (
                <div className="space-y-2">
                  {interviewQuestions.map((item) => (
                    <label
                      key={item.id}
                      className="flex items-start gap-3 p-3 bg-white border border-gray-200 rounded-xl hover:border-gray-300 cursor-pointer transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={checkedQuestions.has(item.id)}
                        onChange={() => handleQuestionToggle(item.id)}
                        className="mt-0.5 w-5 h-5 rounded border-gray-300 text-[#0156FF] focus:ring-[#0156FF] cursor-pointer"
                      />
                      <span className={`flex-1 text-sm ${checkedQuestions.has(item.id) ? 'text-gray-400 line-through' : 'text-gray-900'}`}>
                        {item.question}
                      </span>
                    </label>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 italic">No interview questions configured. Add them in the Posting tab.</p>
              )}
            </div>

            {/* Notes */}
            <div>
              <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-3">
                Interview Notes
              </h2>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Type your impressions here..."
                className="w-full h-64 p-4 bg-gray-50 border border-gray-200 rounded-xl text-base text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#0156FF] focus:border-transparent resize-none"
              />
              <div className="flex items-center justify-between mt-2">
                <p className="text-xs text-gray-500">{notes.length} characters</p>
                {notesSaved && <p className="text-xs text-green-600 font-semibold">Saved!</p>}
              </div>
              <button
                type="button"
                onClick={handleSaveNotes}
                disabled={updateInterview.isPending || !resolvedInterviewId}
                className="mt-3 w-full h-11 rounded-xl bg-[#0156FF] text-white font-semibold text-sm hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {updateInterview.isPending ? 'Saving…' : 'Save Notes'}
              </button>
            </div>

            {/* Rating Slider */}
            <div className="w-full relative pb-2 pt-0">
              <div className="flex items-center justify-between mb-[16px]">
                <label className="text-[19px] font-bold text-[#28292d] leading-[1.4] font-['Nunito']">
                  Overall Rating
                </label>
                <span className="text-[19px] font-bold text-[#28292d] leading-[1.4] font-['Nunito']">
                  {ratingLabels[rating - 1]}
                </span>
              </div>

              <div className="relative h-2 mt-2">
                {/* Custom Track Background */}
                <div className="absolute top-0 left-0 right-0 h-2 bg-[#eeeeee] rounded-full pointer-events-none">
                  {/* Active Track */}
                  <div
                    className="h-full bg-[#0156FF] rounded-full"
                    style={{ width: `${ratingPercentage}%` }}
                  />
                </div>

                {/* Real Range Input (Invisible but interactive) */}
                <input
                  type="range"
                  min="1"
                  max="4"
                  step="1"
                  value={rating}
                  onChange={(e) => setRating(Number(e.target.value))}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                />

                {/* Custom Thumb (Visual Only - follows value) */}
                <div
                  className="absolute top-1/2 w-6 h-6 bg-white border-[3px] border-[#0156FF] rounded-full shadow-sm -translate-y-1/2 pointer-events-none transition-transform active:scale-110"
                  style={{
                    left: `calc(${ratingPercentage}% + ${12 - (24 * (ratingPercentage / 100))}px - 12px)`,
                  }}
                />
              </div>

              {/* Tick Labels */}
              <div className="relative h-6 mt-[8px]">
                {ratingLabels.map((label, index) => {
                  const num = index + 1;
                  const position = (index / 3) * 100;
                  const isFirst = index === 0;
                  const isLast = index === 3;

                  return (
                    <span
                      key={num}
                      className={`absolute top-0 text-[14px] leading-[1.4] font-['Nunito'] whitespace-nowrap ${
                        num === rating
                          ? 'font-bold text-[#0156FF]'
                          : 'font-normal text-black'
                      } ${
                        isFirst ? '' : isLast ? 'transform -translate-x-full' : 'transform -translate-x-1/2'
                      }`}
                      style={{
                        left: isFirst ? '0' : isLast ? '100%' : `calc(${position}% + ${10 - (20 * (position / 100))}px)`
                      }}
                    >
                      {label}
                    </span>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Decision Buttons (only shown on Notes tab) */}
      {activeTab === 'notes' && (
        <div className="bg-white border-t border-gray-200 px-5 py-4">
          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleReject}
              disabled={updateStatus.isPending}
              className="flex-1 h-[40px] rounded-full bg-[#dd1b0a] hover:bg-[#b01608] text-white text-[18px] font-bold shadow-sm transition-colors disabled:opacity-50"
            >
              Reject
            </button>
            <button
              type="button"
              onClick={handleBench}
              disabled={updateStatus.isPending}
              className="flex-1 h-[40px] rounded-full bg-[#FFC107] hover:bg-[#FFB300] text-white text-[18px] font-bold shadow-sm transition-colors disabled:opacity-50"
            >
              Bench
            </button>
            <button
              type="button"
              onClick={handleHire}
              disabled={updateStatus.isPending}
              className="flex-1 h-[40px] rounded-full bg-emerald-500 hover:bg-emerald-600 text-white text-[18px] font-bold shadow-lg transition-colors disabled:opacity-50"
            >
              Hire
            </button>
          </div>
        </div>
      )}

    </div>
  );
}
